import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, Paper, makeStyles } from "@material-ui/core";
import HtmlCoverLetterDisplay from "./HtmlCoverLetterDisplay";
import ATSAnalysis from "./ATSAnalysis"; // Import ATSAnalysis

// Helper function to strip HTML tags and get text content
const stripHtml = (html) => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  } catch (e) {
    console.error("Error stripping HTML:", e);
    return "";
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  atsSection: {
    marginTop: theme.spacing(4),
  },
}));

/**
 * CoverLetter Component
 *
 * A component that displays and allows editing of cover letter content
 * using the HTML editor with templates
 *
 * @param {Object} props - Component props
 * @param {Object} props.content - The cover letter content
 * @param {Function} props.onContentChange - Callback when content changes
 * @param {Object} props.personalInformation - User's personal information
 * @param {Function} props.onPersonalInfoChange - Callback when personal info changes
 * @param {Function} props.handlePrintResume - Function to handle document download
 * @param {boolean} props.downloading - Whether a download is in progress
 * @returns {JSX.Element} The rendered component
 */
const CoverLetter = ({
  content,
  onContentChange,
  personalInformation,
  // onPersonalInfoChange, // This prop is not used in the current component logic for CoverLetter specific changes
  // handlePrintResume, // These props are not directly used by ATS integration part
  // downloading, // but keeping them if CoverLetter component uses them elsewhere
}) => {
  const classes = useStyles();
  const [coverLetterData, setCoverLetterData] = useState(null);
  const [jobKeywords, setJobKeywords] = useState([]);

  // ATS Analysis State
  const [atsFoundKeywords, setAtsFoundKeywords] = useState([]);
  const [atsNotFoundKeywords, setAtsNotFoundKeywords] = useState([]);
  const [atsRememberedKeywords, setAtsRememberedKeywords] = useState(() => {
    try {
      const stored = localStorage.getItem("coverLetterRememberedKeywords");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [atsRememberedNotFoundKeywords, setAtsRememberedNotFoundKeywords] =
    useState([]);
  const [atsTotalKeywords, setAtsTotalKeywords] = useState(0);
  const [atsFoundCount, setAtsFoundCount] = useState(0);
  const [atsFoundRatio, setAtsFoundRatio] = useState(0);
  const [atsScoreLabel, setAtsScoreLabel] = useState("N/A");

  // Prepare data for the HTML editor and extract job keywords
  useEffect(() => {
    if (content && personalInformation) {
      const formatDate = (dateStr) => {
        const dateObj = new Date(dateStr);
        return dateObj.toLocaleDateString(undefined, {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      };

      const combinedData = {
        ...content,
        personalInformation,
        date: content.date
          ? formatDate(content.date)
          : formatDate(new Date().toISOString()),
      };
      setCoverLetterData(combinedData);

      if (content.jobDescription) {
        // Enhanced keyword extraction: lowercasing, alphanumeric, unique, min length 4, common words filter could be added
        const extracted = Array.from(
          new Set(
            content.jobDescription.toLowerCase().match(/\b[a-z\d-]{4,}\b/g) ||
              []
          )
        ).sort();
        setJobKeywords(extracted);
      } else {
        setJobKeywords([]);
      }
    }
  }, [content, personalInformation]);

  // Perform ATS Analysis when cover letter HTML content or job keywords change
  useEffect(() => {
    if (!coverLetterData?.htmlContent && jobKeywords.length === 0) {
      // Reset ATS stats if no content and no job keywords
      setAtsFoundKeywords([]);
      setAtsNotFoundKeywords([]);
      setAtsRememberedNotFoundKeywords([]);
      setAtsTotalKeywords(0);
      setAtsFoundCount(0);
      setAtsFoundRatio(0);
      setAtsScoreLabel("N/A");
      return;
    }

    const coverLetterText = stripHtml(
      coverLetterData?.htmlContent || ""
    ).toLowerCase();

    const currentFoundKws = [];
    const currentNotFoundKws = [];
    jobKeywords.forEach((kw) => {
      if (coverLetterText.includes(kw.toLowerCase())) {
        currentFoundKws.push(kw);
      } else {
        currentNotFoundKws.push(kw);
      }
    });

    const currentRememberedNotFoundKws = [];
    atsRememberedKeywords.forEach((kw) => {
      if (
        !coverLetterText.includes(kw.toLowerCase()) &&
        !currentFoundKws.map((k) => k.toLowerCase()).includes(kw.toLowerCase())
      ) {
        // Add to rememberedNotFound if it's not in main found list and not in CL text
        currentRememberedNotFoundKws.push(kw);
      }
    });

    setAtsFoundKeywords(currentFoundKws);
    setAtsNotFoundKeywords(currentNotFoundKws);
    setAtsRememberedNotFoundKeywords(currentRememberedNotFoundKws);

    const total = jobKeywords.length;
    const found = currentFoundKws.length;
    const ratio = total > 0 ? (found / total) * 100 : 0;

    setAtsTotalKeywords(total);
    setAtsFoundCount(found);
    setAtsFoundRatio(ratio);

    // Adjust score label for cover letters (example thresholds)
    if (total === 0) setAtsScoreLabel("N/A (No Keywords)");
    else if (ratio >= 70) setAtsScoreLabel("Strong"); // Adjusted for CL
    else if (ratio >= 40) setAtsScoreLabel("Fair"); // Adjusted for CL
    else setAtsScoreLabel("Needs Improvement");
  }, [coverLetterData?.htmlContent, jobKeywords, atsRememberedKeywords]);

  // Handle content changes from the HTML editor
  const handleHtmlContentChange = useCallback(
    ({ htmlContent, template }) => {
      if (onContentChange) {
        // Preserve existing content fields, only update htmlContent and template
        const updatedContent = {
          ...content, // current full content object from props
          htmlContent,
          template: template || content.template, // Ensure template is preserved
        };
        onContentChange(updatedContent);
      }
    },
    [onContentChange, content]
  );

  // ATS Analysis callback handlers
  const handleAddKeywordsToCoverLetter = useCallback(
    (keywordsToAdd) => {
      if (!coverLetterData || !onContentChange) return;

      const keywordsText = keywordsToAdd.join(", ");
      // Append as a new paragraph. This is a simple approach.
      // A more sophisticated approach might try to weave them in or use a specific placeholder.
      const newParagraph = `<p><em>Inserted keywords: ${keywordsText}</em></p>`;
      const updatedHtmlContent =
        (coverLetterData.htmlContent || "") + newParagraph;

      const updatedContent = {
        ...content,
        htmlContent: updatedHtmlContent,
        template: coverLetterData.template || content.template,
      };
      onContentChange(updatedContent); // This will trigger ATS re-analysis via useEffect
    },
    [coverLetterData, onContentChange, content]
  );

  const updateRememberedKeywords = useCallback((newRemembered) => {
    setAtsRememberedKeywords(newRemembered);
    try {
      localStorage.setItem(
        "coverLetterRememberedKeywords",
        JSON.stringify(newRemembered)
      );
    } catch (e) {
      console.error("Failed to save remembered keywords to localStorage:", e);
    }
  }, []);

  const handleAddRememberedKeyword = useCallback(
    (keyword) => {
      const trimmedKeyword = keyword.trim();
      if (
        trimmedKeyword &&
        !atsRememberedKeywords
          .map((k) => k.toLowerCase())
          .includes(trimmedKeyword.toLowerCase())
      ) {
        updateRememberedKeywords(
          [...atsRememberedKeywords, trimmedKeyword].sort()
        );
      }
    },
    [atsRememberedKeywords, updateRememberedKeywords]
  );

  const handleRemoveRememberedKeyword = useCallback(
    (keywordToRemove) => {
      updateRememberedKeywords(
        atsRememberedKeywords.filter(
          (kw) => kw.toLowerCase() !== keywordToRemove.toLowerCase()
        )
      );
    },
    [atsRememberedKeywords, updateRememberedKeywords]
  );

  const handleAddMultipleRememberedKeywords = useCallback(
    (keywordsToAdd) => {
      const newKeywords = keywordsToAdd
        .map((kw) => kw.trim())
        .filter(
          (kw) =>
            kw &&
            !atsRememberedKeywords
              .map((k) => k.toLowerCase())
              .includes(kw.toLowerCase())
        );
      if (newKeywords.length > 0) {
        updateRememberedKeywords(
          [...atsRememberedKeywords, ...newKeywords].sort()
        );
      }
    },
    [atsRememberedKeywords, updateRememberedKeywords]
  );

  // Memoize ATSAnalysis props to prevent unnecessary re-renders if possible
  // Moved before the early return to fix ESLint error
  const atsAnalysisProps = useMemo(
    () => ({
      keywords: jobKeywords, // All keywords from job desc
      foundKeywords: atsFoundKeywords,
      notFoundKeywords: atsNotFoundKeywords,
      rememberedKeywords: atsRememberedKeywords,
      rememberedNotFoundKeywords: atsRememberedNotFoundKeywords,
      totalKeywords: atsTotalKeywords,
      foundCount: atsFoundCount,
      foundRatio: atsFoundRatio,
      scoreLabel: atsScoreLabel,
      onAddKeywordsToSkills: handleAddKeywordsToCoverLetter, // Adapted for cover letter
      onAddRememberedKeyword: handleAddRememberedKeyword,
      onRemoveRememberedKeyword: handleRemoveRememberedKeyword,
      onAddMultipleRememberedKeywords: handleAddMultipleRememberedKeywords,
    }),
    [
      jobKeywords,
      atsFoundKeywords,
      atsNotFoundKeywords,
      atsRememberedKeywords,
      atsRememberedNotFoundKeywords,
      atsTotalKeywords,
      atsFoundCount,
      atsFoundRatio,
      atsScoreLabel,
      handleAddKeywordsToCoverLetter,
      handleAddRememberedKeyword,
      handleRemoveRememberedKeyword,
      handleAddMultipleRememberedKeywords,
    ]
  );

  if (!content || typeof content !== "object") {
    // It's better to check coverLetterData for readiness if that's what HtmlCoverLetterDisplay depends on
    return (
      <Typography>
        Loading cover letter data or invalid data provided...
      </Typography>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <Typography variant="h5" className={classes.title}>
          Cover Letter Editor
        </Typography>

        {coverLetterData ? (
          <HtmlCoverLetterDisplay
            coverLetterData={coverLetterData}
            // `keywords` prop for HtmlCoverLetterDisplay might be for a different purpose (e.g. highlighting in editor)
            // For now, it's not directly used by ATS logic here, which uses `jobKeywords`
            keywords={jobKeywords}
            onContentChange={handleHtmlContentChange}
          />
        ) : (
          <Typography>Loading cover letter editor...</Typography>
        )}
      </Paper>

      {coverLetterData &&
        (jobKeywords.length > 0 || atsRememberedKeywords.length > 0) && ( // Show ATS if there's content and (job keywords or remembered keywords)
          <Paper className={`${classes.paper} ${classes.atsSection}`}>
            <ATSAnalysis {...atsAnalysisProps} />
          </Paper>
        )}
    </Box>
  );
};

export default CoverLetter;
